/**
 * Turning a failed IMAP operation into a line the operator can act on.
 *
 * Three dialogs and two panes make the same kind of call against the same mail
 * server, and the answers overlap: a name the server will not take, a folder
 * that has moved since the rail drew it, and the server's own refusal. Keeping
 * the mapping here is what stops them from drifting into five different
 * vocabularies for one problem.
 *
 * The server's raw `reason` is appended rather than swallowed. "The folder could
 * not be created" tells nobody anything; "Mailbox already exists" ends the
 * question, and it is the mail server talking, not us guessing.
 */
import { ApiError } from "../../../../lib/api";

type Translate = (key: string) => string;

/** `useT` answers with the key itself when a catalogue has no entry for it. */
function translated(t: Translate, key: string): string | null {
  const value = t(key);
  return value === key ? null : value;
}

/**
 * The same mapping for a read that failed politely.
 *
 * Listing a folder and reading a message answer 200 with `ok: false` and a
 * reason rather than throwing, because an unreachable mail server is a fact
 * about the mailbox and not a fault in the request. The reason is either one we
 * named ("imap_not_configured") or the server's own sentence, and the two are
 * told apart by whether the catalogue has an entry for it.
 */
export function imapReasonText(t: Translate, reason: string, fallbackKey: string): string {
  return translated(t, `email.imap.reason.${reason}`) ?? `${t(fallbackKey)} ${reason}`;
}

export function imapFolderError(t: Translate, err: unknown, fallbackKey: string): string {
  const fallback = t(fallbackKey);
  if (!(err instanceof ApiError)) return fallback;

  const body = (err.body ?? {}) as { error?: string; reason?: string };

  if (body.error === "invalid_name") {
    return (
      translated(t, `email.imap.nameError.${body.reason ?? "invalid_chars"}`) ??
      t("email.imap.nameError.invalid_chars")
    );
  }

  // `unknown_parent` and `unknown_folder` are the same event from two angles:
  // the list the dialog was drawn from is older than the mailbox. They stay
  // separate because the sentence that helps differs - one names a parent the
  // operator picked, the other the folder they were acting on.
  const known = body.error ? translated(t, `email.imap.opError.${body.error}`) : null;
  if (known) return known;

  return body.reason ? `${fallback} ${body.reason}` : fallback;
}
